import type { UserInfo } from '@/stores/user'
import type { MyRoleOption } from '@/stores/user'

const HOME_CANDIDATES: Array<[permission: string, path: string]> = [
  ['dashboard:read', '/dashboard'],
  ['org:read', '/org'],
  ['emp:read', '/emp'],
  ['att:read', '/att'],
  ['pay:read', '/pay'],
  ['contract:seal:apply', '/contract-seal'],
  ['proc:read', '/proc'],
  ['notify:read', '/notify'],
  ['roles:read', '/roles'],
  ['settings:read', '/settings'],
  ['integration:read', '/integration'],
]

export const ROLE_NAME_MAP: Record<string, string> = {
  SYSTEM_ADMIN: '系统管理员',
  SUPER: '超级管理员',
  SUPER_ADMIN: '超级管理员',
  GROUP_HR: '集团 HR',
  CENTER_HR: '中心 HR',
  FACTORY_HR: '工厂 HR',
  DEPT_MGR: '部门负责人',
  EMP: '员工',
  COMP_MGR: '薪酬经理',
  COMP_DIRECTOR: '薪酬总监',
  FIN: '财务',
  CFO: '首席财务官',
  SEAL_ADMIN: '印章管理员',
}

const ROLE_HOME_MAP: Record<string, string> = {
  SYSTEM_ADMIN: '/roles',
  SUPER: '/dashboard',
  SUPER_ADMIN: '/dashboard',
  GROUP_HR: '/dashboard',
  CENTER_HR: '/org',
  FACTORY_HR: '/emp',
  DEPT_MGR: '/dashboard',
  EMP: '/me/home',
  COMP_MGR: '/pay',
  COMP_DIRECTOR: '/pay',
  FIN: '/pay',
  CFO: '/pay',
  SEAL_ADMIN: '/contract-seal',
}

export function roleLabel(roleCode?: string | null): string {
  if (!roleCode) return '未识别角色'
  return ROLE_NAME_MAP[roleCode] || roleCode
}

/** 角色切换完成、尚未重新拉取权限时使用的保守落点。 */
export function resolveRoleHome(roleCode?: string | null): string {
  if (!roleCode) return '/403'
  return ROLE_HOME_MAP[roleCode] || '/403'
}

/** PC/H5 入口只按真实终端判断，避免桌面浏览器因员工基础角色误进移动端。 */
export function isDesktopClient(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true
  const mobileUa = /android|iphone|ipad|ipod|mobile|windows phone/i.test(navigator.userAgent || '')
  return !mobileUa && window.innerWidth >= 768
}

const DESKTOP_ROLE_PRIORITY = [
  'SYSTEM_ADMIN', 'SUPER_ADMIN', 'SUPER', 'GROUP_HR', 'CENTER_HR', 'FACTORY_HR',
  'DEPT_MGR', 'COMP_DIRECTOR', 'COMP_MGR', 'CFO', 'FIN', 'SEAL_ADMIN',
]

/** 多角色员工在 PC 登录时优先进入其最高优先级管理角色；纯员工账号不强行提权。 */
export function resolveDesktopManagementRole(roles: MyRoleOption[]): MyRoleOption | undefined {
  return [...roles]
    .filter(role => role.code !== 'EMP')
    .sort((a, b) => {
      const ai = DESKTOP_ROLE_PRIORITY.indexOf(a.code)
      const bi = DESKTOP_ROLE_PRIORITY.indexOf(b.code)
      return (ai < 0 ? Number.MAX_SAFE_INTEGER : ai) - (bi < 0 ? Number.MAX_SAFE_INTEGER : bi)
    })[0]
}

/** 防止管理角色被调用方传入的 /me/* 固定落点再次送回 H5。 */
export function resolveRoleSwitchHome(roleCode: string, preferredHome?: string): string {
  if (roleCode === 'EMP') return '/me/home'
  if (preferredHome && !preferredHome.startsWith('/me/')) return preferredHome
  return resolveRoleHome(roleCode)
}

/** 登录完成后跳到当前账号真正可访问的首页，避免固定跳仪表盘后立即进入 403。 */
export function resolveAuthorizedHome(user: UserInfo | null): string {
  if (!user) return '/login'
  const effectiveRole = user.effectiveRoleCode || user.roles?.[0]
  if (effectiveRole === 'EMP' && user.empId) return '/me/home'

  const perms = new Set(user.perms || [])
  const matched = HOME_CANDIDATES.find(([permission]) => perms.has(permission))
  if (matched) return matched[1]
  return user.empId ? '/me/home' : '/403'
}
